import React, {useContext, useEffect, useState, useRef} from "react";
import EditTaskDialog from "./EditTaskDialog";
import EditStepDialog from "./EditStepDialog";
import EditDecisionPointDialog from "./EditDecisionPointDialog";
import DeleteStepDialog from "./DeleteStepDialog";
import AssignTrailDialog from "./AssignTrailDialog";
import {UserContext} from "@context/UserContext";
import {Space} from "antd";
import Button from "@common/Button";
import axios from "axios";
import CircularLoader from "@common/CircularLoader";
import TrailOverviewStage from "./TrailOverviewStage";
import {BrandContext} from "@context/BrandContext";
import EditSurveyDecisionPointDialog from "./EditSurveyDecisionPointDialog";

const TrailBuilder = props => {
    const {trailId, onExit} = props;
    const brandConfig = useContext(BrandContext);
    const {user} = useContext(UserContext);
    const [loading, setLoading] = useState(true);
    const [trailDetails, setTrailDetails] = useState(null);
    const [readOnly, setReadOnly] = useState(false);
    const [trail, setTrail] = useState(null);
    const [dialog, setDialog] = useState(null);
    const [dialogData, setDialogData] = useState(null);
    const trailStageRef = useRef();

    const TRAIL_BUILDER_DIALOGS = {
        ASSIGN_TRAIL: "assignTrail",
        EDIT_TASK: "editTask",
        EDIT_STEP: "editStep",
        DELETE_STEP: "deleteStep",
        EDIT_DECISION_POINT: "editDecisionPoint",
        EDIT_SURVEY_DECISION_POINT: "editSurveyDecisionPoint",
    }

    useEffect(()=> {
        // Expose trails functions for trailBuilder.js
        window.trails = {
            editStepDialog: editStepDialog,
            deleteStepDialog: deleteStepDialog,
            editDecisionPointDialog: editDecisionPointDialog,
        }
        return () => {
            // Cleanup
            delete window.trails;
        };
    },[]);
    useEffect(() => {
        if (trailId == null) return;

        axios.get(`/web/ui/trail/build/${trailId}`)
            .then((response) => {
                setTrailDetails({
                    pathwayId: response.data.trail.pathwayId,
                    pathwayType: response.data.trail.pathwayType,
                    conceptSpecific: response.data.trail.conceptSpecific,
                });
                setReadOnly(response.data.readOnly);
                loadTrailData();
            });
    }, [trailId]);

    const assignTrailDialog = () => {
        setDialog(TRAIL_BUILDER_DIALOGS.ASSIGN_TRAIL);
    }
    const editTaskDialog = () => {
        setDialog(TRAIL_BUILDER_DIALOGS.EDIT_TASK);
    }
    const editStepDialog = (pathwayData) => {
        setDialog(TRAIL_BUILDER_DIALOGS.EDIT_STEP);
        setDialogData(pathwayData);
    }
    const deleteStepDialog = (pathwayData) => {
        setDialog(TRAIL_BUILDER_DIALOGS.DELETE_STEP);
        setDialogData(pathwayData);
    }
    const editDecisionPointDialog = (pathwayData) => {
        setDialog(TRAIL_BUILDER_DIALOGS.EDIT_DECISION_POINT);
        setDialogData(pathwayData);
    }
    const loadTrailData = () => {
        axios.get("/trail/loadTrailData.action", {params: {pathwayId: trailId}})
            .then((response) => {
                if (response.data.success) {
                    const trailObj = {
                        core: response.data.pathway,
                        paths: response.data.paths,
                        concepts: response.data.pathwayConcepts,
                        resources: response.data.resources
                    };
                    window.pathway = trailObj.core;
                    window.pathsObj = trailObj.paths;
                    window.resourcesData = trailObj.resources;
                    window.pathwayBuilder.loadEditor(trailStageRef.current, response.data.readOnly);
                    setReadOnly(response.data.readOnly);
                    setTrail(trailObj);
                } else {
                    console.error('Error loading trail data', trailId, response.data);
                }
            })
            .catch(error => {
                console.error('Error loading trail data', trailId, error);
            })
            .finally(() => {
                setLoading(false);
            })
    }
    const exitBuilder = () => {
        setTrail(null);
        if (onExit && typeof onExit === "function") onExit();
    }

    if (trailId == null) return <></>

    return (
        <div style={{visibility: (loading ? "hidden" : "visible")}}>
            {trail && (
                <>
                    <div className="padding--l">
                        <div className="genBtn-blk">
                            <div className="pull-left">
                                <div className="flexAlignCentre">
                                    <h3 id="pathwayTitle" className="text-article-title pw-text-article-title">
                                        {trail.core.pathwayName}
                                    </h3>
                                    <Space>
                                        {user?.userId === trail.core.ownerId && (
                                            <Button type={"outline"} onClick={() => editTaskDialog()}>Edit</Button>
                                        )}
                                        {(!trailDetails?.conceptSpecific || trail?.core.shared || (user?.userId === trail?.core.ownerId)) && (
                                            <Button onClick={() => assignTrailDialog()}>Assign</Button>
                                        )}
                                        {readOnly && (
                                            <div className="errorMessage">
                                                (Read only - task has been assigned and started)
                                            </div>
                                        )}
                                    </Space>
                                </div>
                            </div>
                            <div className="pull-right">
                                <Button type={"outline"} onClick={() => exitBuilder()}>Back</Button>
                            </div>
                        </div>
                        {trailDetails?.pathwayType === "TASK" && (
                            <div style={{clear: "both"}}>
                                <div>This task contains content from:</div>
                                {trail?.concepts && trail?.concepts.length > 0 && (
                                    <ul>
                                        {trail.concepts.map(concept => (
                                            <li key={concept.conceptId}>
                                                {concept.topicName} &#8658; {concept.conceptName}({brandConfig.conceptName.toLowerCase()})
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>
                    {dialog === TRAIL_BUILDER_DIALOGS.ASSIGN_TRAIL && (
                        <AssignTrailDialog
                            open={true}
                            onClose={() => setDialog(null)}
                            setReload={() => {

                            }}
                            selectedTrail={trail.core}
                        />
                    )}
                    {dialog === TRAIL_BUILDER_DIALOGS.EDIT_TASK && (
                        <EditTaskDialog
                            open={true}
                            data={trail.core}
                            callback={data => {
                                setTrail({...trail, core: {...trail.core, ...data.pathway}});
                            }}
                            onClose={() => setDialog(null)}
                        />
                    )}
                    {dialog === TRAIL_BUILDER_DIALOGS.EDIT_STEP && (
                        <EditStepDialog
                            open={true}
                            data={dialogData}
                            readOnly={readOnly}
                            onClose={() => setDialog(null)}
                        />
                    )}
                    {dialog === TRAIL_BUILDER_DIALOGS.DELETE_STEP && (
                        <DeleteStepDialog
                            open={true}
                            data={dialogData}
                            onClose={() => setDialog(null)}
                        />
                    )}
                    {dialog === TRAIL_BUILDER_DIALOGS.EDIT_DECISION_POINT && dialogData?.resourceType !== "SURVEY" && (
                        <EditDecisionPointDialog
                            open={true}
                            data={dialogData}
                            readOnly={readOnly}
                            onClose={() => setDialog(null)}
                        />
                    )}
                    {dialog === TRAIL_BUILDER_DIALOGS.EDIT_DECISION_POINT && dialogData?.resourceType === "SURVEY" && (
                        <EditSurveyDecisionPointDialog
                            open={true}
                            data={dialogData}
                            readOnly={readOnly}
                            onClose={() => setDialog(null)}
                        />
                    )}
                </>
            )}
            {trailDetails && (
                <div ref={trailStageRef}>
                    <TrailOverviewStage trailDetails={trailDetails} readOnly={readOnly}/>
                </div>
            )}
            {loading && (
                <CircularLoader />
            )}
        </div>
    )
}

export const TrailStepIcon = props => {
    const {icon, title, newStep, endStep, ...other} = props;
    let stepIcon = icon + "-icon";
    let stepTitle = title;
    if (newStep) {

    } else if (endStep) {

    }
    return (
        <div className="flexColumn" {...other}>
            <div className="flexCentre">
                <div className="pw-b-s-icon">
                    {stepIcon && (
                        <div className="icons-svg">
                            <svg viewBox="0 0 128 128" style={{margin: "-9"}}>
                                <use xlinkHref={`#${stepIcon}`} />
                            </svg>
                        </div>
                    )}
                </div>
            </div>
            <div className="flexCentre" style={{position: "relative"}}>
                <div className="pw-b-r-resourceTitle">{stepTitle}</div>
            </div>
        </div>
    )
}

export default TrailBuilder;